/**
 * Tutorial video cards.
 *
 * Wrapped in an IIFE and guarded by customElements.get, because the section renders its
 * script tag once per instance. A page with the section twice runs this file twice, and the
 * old DOMContentLoaded version bound every card twice, so one click opened and closed a panel.
 */
(function () {
  const OPEN_CLASS = 'is-open';

  class TutorialVideos extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.onClick.bind(this);
      this.handleKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      // Listeners live on the element itself, so a theme editor re-render gets a fresh set.
      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.closeAll();
    }

    onClick(event) {
      const toggle = event.target.closest('[data-tutorial-toggle]');
      if (toggle && this.contains(toggle)) {
        const card = toggle.closest('[data-tutorial-card]');
        const type = toggle.getAttribute('data-tutorial-toggle');
        const drop = this.getDrop(card, type);

        if (drop && drop.classList.contains(OPEN_CLASS)) {
          this.close(card, type);
        } else {
          this.open(card, type);
        }
        return;
      }

      const closeBtn = event.target.closest('[data-tutorial-close]');
      if (closeBtn && this.contains(closeBtn)) {
        const drop = closeBtn.closest('[data-tutorial-drop]');
        const card = closeBtn.closest('[data-tutorial-card]');
        // The close button sits inside the panel that is about to go inert, so focus moves back to the toggle.
        if (drop && card) this.close(card, drop.getAttribute('data-tutorial-drop'), true);
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;

      const drop = this.querySelector('[data-tutorial-drop].' + OPEN_CLASS);
      if (!drop) return;

      const card = drop.closest('[data-tutorial-card]');
      this.close(card, drop.getAttribute('data-tutorial-drop'), drop.contains(document.activeElement));
    }

    getDrop(card, type) {
      return card.querySelector('[data-tutorial-drop="' + type + '"]');
    }

    getToggle(card, type) {
      return card.querySelector('[data-tutorial-toggle="' + type + '"]');
    }

    open(card, type) {
      const drop = this.getDrop(card, type);
      if (!drop) return;

      // Only one panel is open at a time across the page, including other instances of this section.
      document.querySelectorAll('tutorial-videos').forEach(function (instance) {
        if (typeof instance.closeAll === 'function') instance.closeAll();
      });

      drop.classList.add(OPEN_CLASS);
      drop.removeAttribute('inert');

      const toggle = this.getToggle(card, type);
      if (toggle) toggle.setAttribute('aria-expanded', 'true');

      this.playMedia(drop);
    }

    close(card, type, restoreFocus) {
      const drop = this.getDrop(card, type);
      if (!drop || !drop.classList.contains(OPEN_CLASS)) return;

      drop.classList.remove(OPEN_CLASS);
      drop.setAttribute('inert', '');

      const toggle = this.getToggle(card, type);
      if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
        if (restoreFocus) toggle.focus();
      }

      this.stopMedia(drop);
    }

    closeAll() {
      this.querySelectorAll('[data-tutorial-drop].' + OPEN_CLASS).forEach((drop) => {
        const card = drop.closest('[data-tutorial-card]');
        if (card) this.close(card, drop.getAttribute('data-tutorial-drop'));
      });
    }

    playMedia(drop) {
      const media = drop.querySelector('[data-tutorial-iframe], [data-tutorial-video]');
      if (!media) return;

      media.src = media.getAttribute('data-src');
      if (media.tagName === 'VIDEO') media.play().catch(function () {});
    }

    stopMedia(drop) {
      const media = drop.querySelector('[data-tutorial-iframe], [data-tutorial-video]');
      if (!media) return;

      if (media.tagName === 'VIDEO') {
        media.pause();
        media.removeAttribute('src');
        media.load();
      } else {
        // An empty src navigates the iframe to about:blank, which stops embedded playback.
        media.src = '';
      }
    }
  }

  if (!customElements.get('tutorial-videos')) {
    customElements.define('tutorial-videos', TutorialVideos);
  }
})();
